import { detectDeviceType } from '@shared/utils/string';
import {
  CommandChannel,
  CommandChannelMsg,
  MediaDevice,
  Stream,
  VideoClient,
} from '@zoom/videosdk';
import { logger } from '../../../utils/logger';
import { getMeetingBrowser } from './capabilities';
import { RECORDER_DISPLAY_NAME } from './constants';

export const excludeRecorder = <T extends { displayName?: string }>(participants?: T[]): T[] =>
  (participants ?? []).filter((participant) => participant.displayName !== RECORDER_DISPLAY_NAME);

const getMediaStreamWithRetry = async (retries: number): Promise<MediaStream> => {
  let attempt = 0;
  let lastError;

  while (attempt < retries) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: {
          aspectRatio: { ideal: 16 / 9 },
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          frameRate: { ideal: 25, max: 30 },
        },
      });
      return stream;
    } catch (err: any) {
      lastError = err;
      if (err.name === 'NotAllowedError') {
        throw err;
      }
      attempt++;

      logger.warn(`getUserMedia attempt ${attempt} failed, retrying...`);

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  throw lastError;
};

export const mountDevices = async (stream?: MediaStream, retries = 1) => {
  let localStream = stream;

  const microphones: MediaDevice[] = [];
  const cameras: MediaDevice[] = [];

  if (!localStream) {
    try {
      localStream = await getMediaStreamWithRetry(retries);
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        throw err;
      }

      try {
        localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        logger.warn(`Camera unavailable (${err?.name}), continuing with audio only`);
      } catch (audioErr: any) {
        if (audioErr?.name === 'NotAllowedError') {
          throw audioErr;
        }
      }
    }
  }

  const devices = await navigator.mediaDevices.enumerateDevices();

  devices.forEach((device) => {
    if (device.kind === 'audioinput') {
      if (device.deviceId !== 'communications') {
        microphones.push({
          deviceId: device.deviceId,
          label: device.label,
        });
      }
    } else if (device.kind === 'videoinput') {
      cameras.push({
        deviceId: device.deviceId,
        label: device.label,
      });
    }
  });

  return {
    microphones,
    cameras,
    stream: localStream,
  };
};

export const findRemovedDevicesById = (oldDevices: MediaDevice[], newDevices: MediaDevice[]) => {
  const newDeviceIds = new Set(newDevices.map((device) => device.deviceId));
  const missingDevice = oldDevices.find((device) => !newDeviceIds.has(device.deviceId));
  return missingDevice ? missingDevice.deviceId : null;
};

export const canStartCamera = async (deviceId: string) => {
  try {
    const constraints = {
      video: { deviceId: { exact: deviceId } },
    };

    const testStream = await navigator.mediaDevices.getUserMedia(constraints);

    testStream.getTracks().forEach((track) => track.stop());
    return { isCameraUsable: true };
  } catch (error: any) {
    return { isCameraUsable: false, error };
  }
};

export const isCameraHDCapable = async (deviceId: string): Promise<boolean> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const device = devices.find((d) => d.deviceId === deviceId && d.kind === 'videoinput');

    if (!device) {
      throw new Error('No camera found with the provided deviceId');
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: device.deviceId } },
    });

    const videoTrack = stream.getVideoTracks()[0];

    const capabilities = videoTrack.getCapabilities();

    stopTracks(stream);

    const widthMax =
      typeof capabilities.width === 'number' ? capabilities.width : capabilities.width?.max;
    const heightMax =
      typeof capabilities.height === 'number' ? capabilities.height : capabilities.height?.max;

    if (!widthMax || !heightMax) {
      return false;
    }

    const isHDCapable = widthMax >= 1280 && heightMax >= 720;

    return isHDCapable;
  } catch (error) {
    logger.error('Error checking if camera is HD capable:', error);
    return false;
  }
};

export const hasScreenShareCapability = () =>
  !!(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function');

export const getStartVideoToastErrors = (error: any): string => {
  switch (error.type) {
    case 'CAN_NOT_DETECT_CAMERA':
      return 'Cannot detect camera device.';
    case 'CAN_NOT_FIND_CAMERA':
      return 'The provided camera device ID is not included in the camera device list.';
    case 'VIDEO_USER_FORBIDDEN_CAPTURE':
      return 'You have forbidden the use of the camera or the camera is taken by other programs.';
    case 'VIDEO_ESTABLISH_STREAM_ERROR':
      return 'Video WebSocket is broken.';
    case 'VIDEO_CAMERA_IS_TAKEN':
      return "Please check if another app is using your camera, then close it, followed by a refresh, if you'd like to be seen.";
    default:
      return error.reason;
  }
};

export const stopTracks = (stream?: MediaStream, filter?: 'audio' | 'video') => {
  const tracks = stream?.getTracks().filter((track) => (filter ? track.kind === filter : true));

  if (!tracks || !tracks.length) return;

  tracks.forEach((track) => track.stop());
};

export const isDeviceBusyError = (error: any) => {
  if (!error) return false;

  const type = error.type ?? '';
  const name = error.name ?? '';
  const reason = `${error.reason ?? ''} ${error.message ?? ''}`.toLowerCase();

  return (
    name === 'NotReadableError' ||
    type === 'VIDEO_CAMERA_IS_TAKEN' ||
    /not\s?readable|is taken|already.*(in )?use|in use|device.*busy|could not start/.test(reason)
  );
};

export const retryOnDeviceBusy = async <T>(
  fn: () => Promise<T>,
  retries = 2,
  delayMs = 400
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt < retries && isDeviceBusyError(error)) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        continue;
      }

      throw error;
    }
  }

  throw lastError;
};

export const subscribeToStatisticData = async (stream: typeof Stream) => {
  const subscribeTasks = [
    stream.subscribeAudioStatisticData(),
    stream.subscribeVideoStatisticData(),
    stream.subscribeShareStatisticData(),
  ];

  for (const task of subscribeTasks) {
    try {
      await task;
    } catch (error) {
      logger.error('Error subscribing to statistic data:', error);
    }
  }
};

export const unsubscribeToStatisticData = async (stream: typeof Stream) => {
  const unsubscribeTasks = [
    stream?.unsubscribeAudioStatisticData(),
    stream?.unsubscribeVideoStatisticData(),
    stream?.unsubscribeShareStatisticData(),
  ];

  for (const task of unsubscribeTasks) {
    try {
      await task;
    } catch (error) {
      logger.error('Error unsubscribing from statistic data:', error);
    }
  }
};

export const removeExtraDecimals = (value?: number) => {
  if (value === undefined) {
    return undefined;
  }

  if (Number.isInteger(value)) {
    return value;
  }

  return parseFloat(value.toFixed(2));
};

export const parseCommandChannelMessage = <T extends object>(text: string): T | null => {
  try {
    const parsed = JSON.parse(text);

    return parsed && typeof parsed === 'object' ? (parsed as T) : null;
  } catch {
    return null;
  }
};

export const getStartAudioOptions = (
  microphoneId?: string,
  backgroundNoiseSuppression?: boolean
) => ({
  microphoneId,
  backgroundNoiseSuppression,
  autoStartAudioInSafari: getMeetingBrowser() === 'safari' && detectDeviceType() !== 'Mobile',
});

type MediaSDKEncDecPayload = {
  action: 'encode' | 'decode';
  type: 'audio' | 'video' | 'share';
  result: 'success' | 'fail';
};

type MediaWorkerState = {
  encode: boolean;
  decode: boolean;
  failed: boolean;
  isTracking: boolean;
  waiters: Array<() => void>;
};

const audioWorkerState = new WeakMap<typeof VideoClient, MediaWorkerState>();

const getAudioWorkerState = (zoomClient: typeof VideoClient) => {
  let state = audioWorkerState.get(zoomClient);

  if (!state) {
    state = { encode: false, decode: false, failed: false, isTracking: false, waiters: [] };
    audioWorkerState.set(zoomClient, state);
  }

  return state;
};

const isAudioWorkerReady = (state: MediaWorkerState) =>
  state.failed || (state.encode && state.decode);

export const trackAudioEncodeDecodeReadiness = (zoomClient?: typeof VideoClient) => {
  if (!zoomClient) return;

  const state = getAudioWorkerState(zoomClient);

  if (state.isTracking) return;

  state.isTracking = true;

  zoomClient.on('media-sdk-change', (payload: MediaSDKEncDecPayload) => {
    if (payload.type !== 'audio') return;

    if (payload.result === 'fail') {
      logger.warn('Zoom audio encode/decode reported failure.', payload);
      state.failed = true;
    } else {
      state[payload.action] = true;
    }

    if (isAudioWorkerReady(state)) {
      state.waiters.splice(0).forEach((resolve) => resolve());
    }
  });
};

export const waitForAudioEncodeDecodeReady = (
  zoomClient?: typeof VideoClient,
  timeoutMs = 5000
) => {
  if (!zoomClient) {
    return Promise.resolve();
  }

  trackAudioEncodeDecodeReadiness(zoomClient);

  const state = getAudioWorkerState(zoomClient);

  if (isAudioWorkerReady(state)) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    let resolved = false;

    const resolveOnce = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      state.waiters = state.waiters.filter((waiter) => waiter !== resolveOnce);
      resolve();
    };

    state.waiters.push(resolveOnce);

    const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
      logger.debug('Zoom audio encode/decode readiness timeout elapsed, continuing.');
      resolveOnce();
    }, timeoutMs);
  });
};

const ZOOM_ERROR_CODES = {
  MEETING_CLOSED: 5002,
  USER_NOT_SENDING_VIDEO: 6001,
  VIRTUAL_BACKGROUND_PREVIEW: 6114,
  USER_DENIED_SCREEN_SHARE: 6200,
  COMMAND_CHANNEL_NOT_READY: 7104,
};

export const getZoomErrorCode = (error: unknown): number | undefined => {
  const code = (error as { errorCode?: unknown })?.errorCode;
  return typeof code === 'number' ? code : undefined;
};

export const isCommandChannelNotReadyError = (error: unknown) =>
  getZoomErrorCode(error) === ZOOM_ERROR_CODES.COMMAND_CHANNEL_NOT_READY;

export const isExpectedZoomError = (error: unknown) => {
  const code = getZoomErrorCode(error);
  const reason = String((error as { reason?: unknown })?.reason ?? '');

  return (
    code === ZOOM_ERROR_CODES.MEETING_CLOSED ||
    code === ZOOM_ERROR_CODES.USER_NOT_SENDING_VIDEO ||
    code === ZOOM_ERROR_CODES.USER_DENIED_SCREEN_SHARE ||
    code === ZOOM_ERROR_CODES.COMMAND_CHANNEL_NOT_READY ||
    (code === ZOOM_ERROR_CODES.VIRTUAL_BACKGROUND_PREVIEW && reason.includes('before start'))
  );
};

export const sendCommandChannelMessage = async (
  commandChannel: typeof CommandChannel | undefined,
  text: string
): Promise<CommandChannelMsg | undefined> => {
  if (!commandChannel) return undefined;

  try {
    return (await commandChannel.send(text)) as CommandChannelMsg;
  } catch (error) {
    if (isCommandChannelNotReadyError(error)) return undefined;
    throw error;
  }
};
